import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Paperclip, X, FileText } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Multi-file picker with "Adjuntar otro archivo" button and removable chips.
 * Props:
 *   files: File[] (controlled list)
 *   setFiles: (newFiles) => void
 *   maxFiles?: number (default 10)
 *   testIdPrefix?: string
 *   buttonLabel?: string
 *   firstButtonLabel?: string
 *   accept?: string (e.g. "video/*"); when set, doesn't show "Adjuntar OTRO" button label (uses custom)
 */
export default function MultiFilePicker({
  files = [],
  setFiles,
  maxFiles = 10,
  accept,
  testIdPrefix = 'multi-file',
  buttonLabel = 'Adjuntar otro archivo',
  firstButtonLabel = 'Adjuntar archivo',
}) {
  const inputRef = useRef(null);

  const handlePick = (e) => {
    const picked = Array.from(e.target.files || []);
    const merged = [...files];
    for (const f of picked) {
      if (merged.length >= maxFiles) {
        toast.error(`Máximo ${maxFiles} archivos`);
        break;
      }
      // dedupe by name+size
      if (!merged.some(x => x.name === f.name && x.size === f.size)) {
        merged.push(f);
      }
    }
    setFiles(merged);
    // reset input so same file can be picked again if removed
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeAt = (idx) => {
    const next = files.slice();
    next.splice(idx, 1);
    setFiles(next);
  };

  const atLimit = files.length >= maxFiles;

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        onChange={handlePick}
        className="hidden"
        data-testid={`${testIdPrefix}-input`}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={atLimit}
        onClick={() => inputRef.current?.click()}
        className="gap-2 rounded-lg"
        data-testid={`${testIdPrefix}-pick-btn`}
      >
        <Paperclip className="w-4 h-4" />
        {files.length === 0 ? firstButtonLabel : buttonLabel}
        {files.length > 0 && (
          <span className="ml-1 text-[10px] text-slate-400">({files.length}/{maxFiles})</span>
        )}
      </Button>

      {files.length > 0 && (
        <ul className="space-y-1.5" data-testid={`${testIdPrefix}-list`}>
          {files.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg text-sm text-slate-600"
              data-testid={`${testIdPrefix}-item-${i}`}
            >
              <FileText className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
              <span className="truncate flex-1">{f.name}</span>
              <span className="text-[10px] text-slate-400">{(f.size / 1024).toFixed(0)} KB</span>
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="text-slate-300 hover:text-red-500 transition-colors"
                title="Eliminar"
                data-testid={`${testIdPrefix}-remove-${i}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
